import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, CircleAlert, Clock, Download, Gauge, Hourglass, Inbox, MessageSquareQuote, Star, Tags, Timer, TriangleAlert, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { BarList, ChartLegend, formatHeatmapDay, HeatmapLegend, HeatmapRow, StackedBarList, TrendChart } from '../components/charts';
import { CostsReportTab, MaintenanceReportTab, ReplacementReportTab } from '../components/facilities-reports';
import { RatingStars } from '../components/ticket-tracking';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  PageLoader,
  Segmented,
  Select,
  StatCard,
  Table,
  Tabs,
  Td,
  Th,
} from '../components/ui';
import { api, apiUrl, errorMessage } from '../lib/api';
import { indentedName, useDepartments } from '../lib/automation';
import { useDesk } from '../lib/desk';
import { formatMoney, formatNumber, formatRelative } from '../lib/format';
import { useUrlState } from '../lib/hooks';
import {
  formatHours,
  formatHoursLogged,
  formatPercent,
  formatRating,
  JOB_KIND_LABEL,
  periodLabel,
  RATING_LABEL,
  REPORT_SOURCE_LABEL,
  type AutomationJob,
  type AutomationStatusReport,
  type JobKind,
  type OverviewReport,
  type ReportDays,
  type SatisfactionReport,
  type TechnicianReport,
  type TimeGroup,
  type TimeReport,
  type VolumeReport,
} from '../lib/reports';
import type { HelpdeskSettings, WorkOrderPriority, WorkOrderSource } from '../lib/types';
import { WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL } from '../lib/work-orders';

interface Filters {
  days: ReportDays;
  departmentId?: string;
}

type Extra = Record<string, string | undefined>;

function useReport<T>(name: string, filters: Filters, extra: Extra = {}) {
  const { service } = useDesk();
  return useQuery({
    queryKey: ['report', service, name, filters, extra],
    queryFn: () => api.get<T>(`/reports/${name}`, { service, days: filters.days, departmentId: filters.departmentId, ...extra }),
    placeholderData: keepPreviousData,
  });
}

function ExportLink({ report, filters, extra }: { report: string; filters?: Filters; extra?: Extra }) {
  const { service } = useDesk();
  return (
    <a href={apiUrl(`/reports/export/${report}.csv`, { service, days: filters?.days, departmentId: filters?.departmentId, ...extra })}>
      <Button size="sm" variant="ghost" icon={<Download />}>
        CSV
      </Button>
    </a>
  );
}

const PRIORITIES: WorkOrderPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const PRIORITY_CLASS: Record<WorkOrderPriority, string> = { URGENT: 'bg-red-500', HIGH: 'bg-amber-400', NORMAL: 'bg-brand-500', LOW: 'bg-slate-300' };

const prioritySegments = (byPriority: Record<WorkOrderPriority, number>) =>
  PRIORITIES.map((priority) => ({ key: priority, value: byPriority[priority], label: WORK_ORDER_PRIORITY_LABEL[priority], className: PRIORITY_CLASS[priority] }));

function PriorityLegend() {
  return (
    <div className="px-5 pt-4">
      <ChartLegend items={PRIORITIES.map((priority) => ({ label: WORK_ORDER_PRIORITY_LABEL[priority], className: PRIORITY_CLASS[priority] }))} />
    </div>
  );
}

function ReportState({ query }: { query: { isLoading: boolean; error: unknown; data?: unknown; refetch(): unknown } }) {
  if (query.isLoading) return <PageLoader />;
  return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({ filters }: { filters: Filters }) {
  const desk = useDesk();
  const report = useReport<OverviewReport>('overview', filters);
  if (!report.data) return <ReportState query={report} />;
  const { totals, resolutionTrend, volumeByBuilding, workload, openByDepartment } = report.data;
  const period = periodLabel(filters.days);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Open now"
          value={formatNumber(totals.open)}
          sub={`${formatNumber(totals.unassigned)} unassigned · ${formatNumber(totals.awaitingFirstResponse)} waiting for a first reply`}
          icon={<Inbox />}
          to={`${desk.base}/work-orders`}
        />
        <StatCard
          label="Opened"
          value={formatNumber(totals.created)}
          sub={`In ${period} · ${formatNumber(totals.resolved)} resolved, ${formatNumber(totals.reopened)} reopened`}
          icon={<Activity />}
          tone="violet"
        />
        <StatCard
          label="Time to resolve"
          value={formatHours(totals.resolutionHours.average)}
          sub={totals.resolved ? `Average · median ${formatHours(totals.resolutionHours.median)}` : 'Nothing resolved in this period'}
          icon={<Hourglass />}
          tone="amber"
        />
        <StatCard
          label="First response"
          value={formatHours(totals.firstResponseHours.average)}
          sub={
            totals.firstResponseHours.average === null
              ? 'No portal or email tickets answered in this period'
              : totals.satisfaction.responses
                ? `Average · ${formatPercent(totals.satisfaction.satisfiedRate)} of requesters satisfied`
                : `Average · median ${formatHours(totals.firstResponseHours.median)}`
          }
          icon={<Timer />}
          tone="green"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader
            title="Resolution time"
            description={`Average time from opening to resolved, for tickets resolved in ${period}`}
            actions={<ExportLink report="resolution-time" filters={filters} />}
          />
          <div className="p-5">
            {totals.resolved === 0 ? (
              <EmptyState icon={<Hourglass />} title="No tickets resolved in this period" />
            ) : (
              <TrendChart
                points={resolutionTrend.map((point) => ({ key: point.key, label: point.label, count: point.resolved, value: point.average }))}
                countLabel="Tickets resolved"
                valueLabel="Average time to resolve"
                formatValue={formatHours}
              />
            )}
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader title="Volume by building" description={`Tickets opened in ${period}`} actions={<ExportLink report="buildings" filters={filters} />} />
          <BarList
            items={volumeByBuilding.map((row) => ({
              key: row.buildingId ?? 'none',
              label: row.name,
              value: row.created,
              secondary: row.resolved
                ? `${formatNumber(row.open)} open now · resolved in ${formatHours(row.resolutionHours.average)} on average`
                : `${formatNumber(row.open)} open now`,
            }))}
            emptyText="No tickets in this period"
          />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Who is carrying what" description="Open tickets by assignee and priority" actions={<ExportLink report="workload" filters={filters} />} />
          <PriorityLegend />
          <StackedBarList
            items={workload.map((row) => ({
              key: row.userId ?? 'unassigned',
              label: row.name,
              segments: prioritySegments(row.byPriority),
              secondary: [
                `oldest opened ${row.oldestOpenDays === 0 ? 'today' : `${row.oldestOpenDays} day${row.oldestOpenDays === 1 ? '' : 's'} ago`}`,
                row.awaitingFirstResponse > 0 && `${row.awaitingFirstResponse} waiting for a first reply`,
              ]
                .filter(Boolean)
                .join(' · ')
                .replace(/^./, (c) => c.toUpperCase()),
            }))}
            emptyText="No open tickets"
          />
        </Card>
        <Card>
          <CardHeader
            title={`Open tickets by ${desk.departmentWord}`}
            description={desk.facilities ? 'Includes sub-categories' : 'Includes sub-departments'}
            actions={<ExportLink report="departments" filters={filters} />}
          />
          <PriorityLegend />
          <StackedBarList
            items={openByDepartment.map((row) => ({
              key: row.departmentId ?? 'none',
              label: row.path,
              segments: prioritySegments(row.byPriority),
              to: `${desk.base}/work-orders?departmentId=${row.departmentId ?? 'none'}`,
            }))}
            emptyText="No open tickets"
          />
        </Card>
      </div>
      <p className="text-xs text-slate-500">
        Times are calendar hours. First response counts tickets reported through the portal or by email. Merged duplicates aren’t counted.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Technicians
// ---------------------------------------------------------------------------

function TechniciansTab({ filters }: { filters: Filters }) {
  const report = useReport<TechnicianReport>('technicians', filters);
  if (!report.data) return <ReportState query={report} />;
  const { team, technicians } = report.data;
  const period = periodLabel(filters.days);

  return (
    <div className="space-y-6">
      {team ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Tickets resolved" value={formatNumber(team.resolved)} sub={team.resolved ? `In ${period} · ${formatHours(team.resolutionHours.average)} on average` : `In ${period}`} icon={<Gauge />} />
          <StatCard
            label="First response"
            value={formatHours(team.firstResponseHours.average)}
            sub={team.firstResponseHours.average === null ? 'No portal or email tickets answered in this period' : `Average · median ${formatHours(team.firstResponseHours.median)}`}
            icon={<Timer />}
            tone="green"
          />
          <StatCard
            label="First-touch resolution"
            value={formatPercent(team.firstTouchRate)}
            sub={`${formatNumber(team.firstTouchResolved)} of ${formatNumber(team.firstTouchEligible)} resolved tickets`}
            icon={<Star />}
            tone="violet"
          />
          <StatCard
            label="Hours logged"
            value={formatHoursLogged(team.minutesLogged)}
            sub={team.satisfaction.responses ? `Satisfaction ${formatRating(team.satisfaction.average)}` : 'No survey responses yet'}
            icon={<Clock />}
            tone="amber"
          />
        </div>
      ) : (
        <Alert tone="blue">These are your own numbers. Administrators see the whole team.</Alert>
      )}

      <Card>
        <CardHeader
          title="Technician performance"
          description={`Tickets resolved in ${period}, counted for whoever resolved them.`}
          actions={<ExportLink report="technicians" filters={filters} />}
        />
        {technicians.length === 0 ? (
          <EmptyState icon={<Users />} title="No technician activity in this period" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Technician</Th>
                <Th className="text-right">Resolved</Th>
                <Th className="text-right">Time to resolve</Th>
                <Th className="text-right">First response</Th>
                <Th className="text-right">First touch</Th>
                <Th className="text-right">Open now</Th>
                <Th className="text-right">Hours logged</Th>
                <Th className="text-right">Satisfaction</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {technicians.map((row) => (
                <tr key={row.userId}>
                  <Td className="font-medium whitespace-nowrap text-slate-900">{row.name}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(row.resolved)}</Td>
                  <Td className="text-right tabular-nums">
                    {formatHours(row.resolutionHours.average)}
                    <span className="block text-xs text-slate-400">median {formatHours(row.resolutionHours.median)}</span>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatHours(row.firstResponseHours.average)}
                    <span className="block text-xs text-slate-400">
                      {formatNumber(row.firstResponses)} first repl{row.firstResponses === 1 ? 'y' : 'ies'}
                    </span>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatPercent(row.firstTouchRate)}
                    <span className="block text-xs text-slate-400">
                      {row.firstTouchResolved} of {row.firstTouchEligible}
                    </span>
                  </Td>
                  <Td className="text-right tabular-nums">{formatNumber(row.openNow)}</Td>
                  <Td className="text-right tabular-nums">{formatHoursLogged(row.minutesLogged)}</Td>
                  <Td className="text-right tabular-nums">
                    {row.satisfaction.responses ? formatRating(row.satisfaction.average) : '—'}
                    <span className="block text-xs text-slate-400">
                      {formatNumber(row.satisfaction.responses)} response{row.satisfaction.responses === 1 ? '' : 's'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <p className="text-xs text-slate-500">
        First-touch resolution: resolved without being reopened or reassigned, with at most one reply to the requester and nothing more needed from
        them. Tickets opened by smart rules and schedules aren’t counted.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Satisfaction
// ---------------------------------------------------------------------------

function SatisfactionTable({ rows, label }: { rows: ({ key: string; name: string; sent: number; responseRate: number | null } & SatisfactionReport['totals'])[]; label: string }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>{label}</Th>
          <Th className="text-right">Sent</Th>
          <Th className="text-right">Answered</Th>
          <Th className="text-right">Average</Th>
          <Th className="text-right">Satisfied</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.key}>
            <Td className="text-slate-900">{row.name}</Td>
            <Td className="text-right tabular-nums">{formatNumber(row.sent)}</Td>
            <Td className="text-right tabular-nums">{formatPercent(row.responseRate)}</Td>
            <Td className="text-right tabular-nums">{formatRating(row.average)}</Td>
            <Td className="text-right tabular-nums">{formatPercent(row.satisfiedRate)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function SatisfactionTab({ filters }: { filters: Filters }) {
  const desk = useDesk();
  const can = desk.can;
  const report = useReport<SatisfactionReport>('satisfaction', filters);
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const period = periodLabel(filters.days);

  if (data.surveyDepartments.length === 0 && data.totals.sent === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Star />}
          title="Satisfaction surveys are off"
          description={`Turn surveys on for the ${desk.facilities ? 'categories' : 'departments'} you want feedback on. When a ticket there is resolved, the requester is asked to rate it.`}
          action={
            can('TECHNICIAN') && (
              <Link to={desk.facilities ? '/facilities/setup' : '/helpdesk-setup'}>
                <Button>{desk.facilities ? 'Open facilities setup' : 'Open help desk setup'}</Button>
              </Link>
            )
          }
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Satisfied" value={formatPercent(data.totals.satisfiedRate)} sub="Rated 4 or 5 out of 5" icon={<Star />} tone="green" />
        <StatCard
          label="Average rating"
          value={formatRating(data.totals.average)}
          sub={`${formatNumber(data.totals.responses)} response${data.totals.responses === 1 ? '' : 's'} in ${period}`}
          icon={<MessageSquareQuote />}
        />
        <StatCard label="Response rate" value={formatPercent(data.totals.responseRate)} sub={`${formatNumber(data.totals.sent)} surveys sent`} icon={<Inbox />} tone="violet" />
        <StatCard
          label="Surveys on in"
          value={`${formatNumber(data.surveyDepartments.length)} department${data.surveyDepartments.length === 1 ? '' : 's'}`}
          sub={data.surveyDepartments.slice(0, 3).join(', ') + (data.surveyDepartments.length > 3 ? ` and ${data.surveyDepartments.length - 3} more` : '') || 'None right now'}
          icon={<Tags />}
          tone="amber"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader title="Satisfaction over time" description="Average rating by when requesters answered" />
          <div className="p-5">
            {data.totals.responses === 0 ? (
              <EmptyState icon={<MessageSquareQuote />} title="No responses in this period" />
            ) : (
              <TrendChart
                points={data.trend.map((point) => ({ key: point.key, label: point.label, count: point.responses, value: point.average }))}
                countLabel="Responses"
                valueLabel="Average rating"
                formatValue={(value) => value.toFixed(1)}
              />
            )}
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader title="Ratings" />
          <BarList
            items={[5, 4, 3, 2, 1].map((rating) => ({
              key: String(rating),
              label: `${rating} · ${RATING_LABEL[rating]}`,
              value: data.distribution.find((d) => d.rating === rating)?.count ?? 0,
              tone: rating >= 4 ? ('green' as const) : rating === 3 ? ('amber' as const) : ('red' as const),
            }))}
          />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="By department" description="Where the ticket was when it was resolved" />
          <SatisfactionTable label="Department" rows={data.byDepartment.map((row) => ({ ...row, key: row.departmentId ?? 'none', name: row.path }))} />
        </Card>
        {data.byTechnician.length > 0 && (
          <Card>
            <CardHeader title="By technician" description="Who resolved the ticket" />
            <SatisfactionTable label="Technician" rows={data.byTechnician.map((row) => ({ ...row, key: row.userId }))} />
          </Card>
        )}
      </div>

      <Card>
        <CardHeader title="What requesters said" description="Most recent responses" actions={<ExportLink report="satisfaction" filters={filters} />} />
        {data.responses.length === 0 ? (
          <EmptyState icon={<MessageSquareQuote />} title="No responses in this period" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.responses.map((response) => (
              <li key={response.workOrder.id} className="flex flex-wrap gap-x-4 gap-y-1 px-5 py-3">
                <RatingStars rating={response.rating} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <Link to={desk.workOrderHref(response.workOrder.id)} className="text-sm font-medium text-slate-900 hover:text-brand-700">
                    <span className="text-slate-400">#{response.workOrder.number}</span> {response.workOrder.title}
                  </Link>
                  {response.comment && <p className="mt-0.5 text-sm whitespace-pre-wrap text-slate-600">“{response.comment}”</p>}
                  <p className="mt-0.5 text-xs text-slate-500">
                    {[response.department, response.technician, formatRelative(response.respondedAt)].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time logged
// ---------------------------------------------------------------------------

function HourlyRateCard() {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['helpdesk-settings', desk.service], queryFn: () => api.get<HelpdeskSettings>('/helpdesk/settings', desk.query()) });
  const [rate, setRate] = useState('');
  useEffect(() => setRate(settings.data?.hourlyRate?.toString() ?? ''), [settings.data?.hourlyRate]);

  const save = useMutation({
    mutationFn: () => api.put(desk.path('/helpdesk/hourly-rate'), { hourlyRate: rate.trim() === '' ? null : Number(rate) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['helpdesk-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['report'] });
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Hourly rate saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="flex flex-wrap items-end gap-3 p-5"
      >
        <Field
          label="Hourly rate for labor cost"
          hint={`Turns logged time into cost ${desk.facilities ? 'for budget planning' : 'for cost recovery'}. Only ${desk.team} sees it.`}
        >
          <Input type="number" min={0} step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 45" className="w-40" />
        </Field>
        <Button type="submit" loading={save.isPending}>
          Save rate
        </Button>
      </form>
    </Card>
  );
}

function TimeGroupCard({ title, description, groups }: { title: string; description?: string; groups: TimeGroup[] }) {
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <BarList
        items={groups.map((group) => ({
          key: group.key,
          label: group.label,
          value: group.minutes,
          display: formatHoursLogged(group.minutes),
          secondary: `${formatNumber(group.tickets)} ticket${group.tickets === 1 ? '' : 's'}${group.cost !== null ? ` · ${formatMoney(group.cost)}` : ''}`,
        }))}
        emptyText="No time logged in this period"
      />
    </Card>
  );
}

function TimeTab({ filters }: { filters: Filters }) {
  const desk = useDesk();
  const can = desk.can;
  const report = useReport<TimeReport>('time', filters);
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const period = periodLabel(filters.days);

  return (
    <div className="space-y-6">
      {data.onlyMine && <Alert tone="blue">This is the time you logged. Administrators see the whole team.</Alert>}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Hours logged" value={formatHoursLogged(data.totals.minutes)} sub={`${formatNumber(data.totals.entries)} entr${data.totals.entries === 1 ? 'y' : 'ies'} in ${period}`} icon={<Clock />} />
        <StatCard
          label="Tickets worked"
          value={formatNumber(data.totals.tickets)}
          sub={data.totals.tickets ? `${formatHoursLogged(Math.round(data.totals.minutes / data.totals.tickets))} per ticket` : 'Nothing logged yet'}
          icon={<Inbox />}
          tone="violet"
        />
        <StatCard
          label="Labor cost"
          value={data.totals.cost === null ? '—' : formatMoney(data.totals.cost)}
          sub={data.hourlyRate ? `At ${formatMoney(data.hourlyRate)} an hour` : 'Set an hourly rate to see cost'}
          icon={<Gauge />}
          tone="green"
        />
      </div>
      {can('ADMIN') && <HourlyRateCard />}
      <Card>
        <CardHeader title="Hours logged over time" description="By the day the work happened" actions={<ExportLink report="time" filters={filters} />} />
        <div className="p-5">
          {data.totals.minutes === 0 ? (
            <EmptyState icon={<Clock />} title="No time logged in this period" description="Technicians log time from the Time card on each ticket." />
          ) : (
            <TrendChart
              points={data.trend.map((point) => ({ key: point.key, label: point.label, count: Math.round((point.minutes / 60) * 10) / 10, value: null }))}
              countLabel="Hours logged"
              valueLabel="Hours logged"
              formatValue={(value) => `${value}h`}
            />
          )}
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-3">
        {!data.onlyMine && <TimeGroupCard title="By technician" groups={data.byTechnician} />}
        <TimeGroupCard
          title={desk.facilities ? 'By category' : 'By team'}
          description={desk.facilities ? 'The work order’s category' : 'The ticket’s department'}
          groups={data.byTeam}
        />
        <TimeGroupCard title="By building" description={desk.facilities ? 'Where the work was' : 'Where the device or room is'} groups={data.byBuilding} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticket volume
// ---------------------------------------------------------------------------

/** Where each help desk's tickets come from. */
const DESK_SOURCES: Record<'IT' | 'FACILITIES', WorkOrderSource[]> = {
  IT: ['REQUESTER', 'EMAIL', 'STAFF', 'SMART_RULE', 'RECURRING'],
  FACILITIES: ['REQUESTER', 'EMAIL', 'STAFF', 'PREVENTIVE', 'INSPECTION'],
};
const SOURCE_CLASS: Record<WorkOrderSource, string> = {
  REQUESTER: 'bg-brand-500',
  EMAIL: 'bg-teal-400',
  STAFF: 'bg-sky-300',
  SMART_RULE: 'bg-violet-400',
  RECURRING: 'bg-amber-300',
  PREVENTIVE: 'bg-emerald-400',
  INSPECTION: 'bg-rose-300',
};

function SourceColumns({ points }: { points: VolumeReport['sourceTrend'] }) {
  const SOURCES = DESK_SOURCES[useDesk().service];
  const totals = points.map((point) => SOURCES.reduce((sum, source) => sum + point[source], 0));
  const max = Math.max(1, ...totals);
  return (
    <figure className="space-y-2">
      <div className="flex h-44 items-end gap-0.5 border-b border-slate-200">
        {points.map((point, index) => (
          <div
            key={point.key}
            className="flex h-full min-w-0 flex-1 flex-col justify-end"
            title={`${point.label}: ${totals[index]} ticket${totals[index] === 1 ? '' : 's'}${SOURCES.filter((s) => point[s]).map((s) => `, ${point[s]} ${REPORT_SOURCE_LABEL[s].toLowerCase()}`).join('')}`}
          >
            {[...SOURCES].reverse().map((source) =>
              point[source] > 0 ? <div key={source} className={SOURCE_CLASS[source]} style={{ height: `${(point[source] / max) * 100}%` }} /> : null,
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{points[0]?.label}</span>
        <span>{points.at(-1)?.label}</span>
      </div>
      <ChartLegend items={SOURCES.map((source) => ({ label: REPORT_SOURCE_LABEL[source], className: SOURCE_CLASS[source] }))} />
    </figure>
  );
}

function VolumeTab({ filters }: { filters: Filters }) {
  const desk = useDesk();
  const can = desk.can;
  const [fieldId, setFieldId] = useState('');
  const report = useReport<VolumeReport>('volume', filters, { fieldId: fieldId || undefined });
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const period = periodLabel(filters.days);
  const byField = data.byField;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader title="Tickets by source" description={`${formatNumber(data.total)} ticket${data.total === 1 ? '' : 's'} opened in ${period}`} actions={<ExportLink report="sources" filters={filters} />} />
          <div className="p-5">
            {data.total === 0 ? <EmptyState icon={<Inbox />} title="No tickets in this period" /> : <SourceColumns points={data.sourceTrend} />}
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader
            title="Where tickets come from"
            description={
              desk.facilities
                ? 'Requests and email come from staff; preventive maintenance schedules and failed inspections open work orders on their own'
                : 'Portal and email tickets come from requesters; smart rules and schedules open tickets automatically'
            }
          />
          <BarList
            items={data.bySource.map((row) => ({
              key: row.source,
              label: REPORT_SOURCE_LABEL[row.source],
              value: row.count,
              secondary: data.total ? `${formatPercent((row.count / data.total) * 100)} of tickets` : undefined,
            }))}
          />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Tickets by field"
            description={byField ? `Grouped by ${byField.field.name}` : 'Group tickets by a field your technicians fill in, like damage type'}
            actions={
              byField && (
                <>
                  <Select value={byField.field.id} onChange={(e) => setFieldId(e.target.value)} className="w-44" aria-label="Group by field">
                    {data.fields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.name}
                        {field.archived ? ' (archived)' : ''}
                      </option>
                    ))}
                  </Select>
                  <ExportLink report="field" filters={filters} extra={{ fieldId: byField.field.id }} />
                </>
              )
            }
          />
          {byField ? (
            <BarList
              items={[
                ...byField.rows.map((row) => ({ key: row.value, label: row.label, value: row.count, secondary: row.open ? `${formatNumber(row.open)} still open` : undefined })),
                ...(byField.notFilledIn > 0 ? [{ key: '__none', label: 'Not filled in', value: byField.notFilledIn, tone: 'slate' as const }] : []),
              ]}
              emptyText="No tickets in this period"
            />
          ) : (
            <EmptyState
              icon={<Tags />}
              title="No ticket fields yet"
              description={
                desk.facilities
                  ? 'Add fields such as the kind of repair in Facilities setup. Technicians fill them in on each work order.'
                  : 'Add fields such as damage type in Help desk setup. Technicians fill them in on each ticket.'
              }
              action={
                can('ADMIN') && (
                  <Link to={`${desk.facilities ? '/facilities/setup' : '/helpdesk-setup'}?tab=fields`}>
                    <Button>Add a ticket field</Button>
                  </Link>
                )
              }
            />
          )}
        </Card>
        {!desk.facilities && (
          <Card>
            <CardHeader title="Tickets by problem type" description="What requesters chose when reporting" />
            <BarList
              items={data.byCategory.map((row) => ({ key: row.category, label: WORK_ORDER_CATEGORY_LABEL[row.category], value: row.count }))}
              emptyText="No tickets in this period"
            />
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Automation status
// ---------------------------------------------------------------------------

const JOB_LINK: Record<JobKind, (job: AutomationJob) => string> = {
  SYNC: () => '/integrations',
  SMART_RULE: () => '/automation',
  RECURRING: () => '/automation',
  WORKFLOW: (job) => `/automation/workflows/${job.id}`,
};

function AutomationTab() {
  const report = useQuery({
    queryKey: ['report', 'automation'],
    queryFn: () => api.get<AutomationStatusReport>('/reports/automation'),
    refetchInterval: 60_000,
  });
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const groups = (['SYNC', 'SMART_RULE', 'RECURRING', 'WORKFLOW'] as JobKind[])
    .map((kind) => ({ kind, jobs: data.jobs.filter((job) => job.kind === kind) }))
    .filter((group) => group.jobs.length > 0);
  const queueStuck = !!data.queue.oldestQueuedAt && Date.now() - new Date(data.queue.oldestQueuedAt).getTime() > 5 * 60_000;

  return (
    <div className="space-y-6">
      {(data.summary.overdue > 0 || queueStuck) && (
        <Alert tone="red" icon={<TriangleAlert />} title="Scheduled work is running behind">
          {data.summary.overdue > 0 && `${data.summary.overdue} job${data.summary.overdue === 1 ? ' is' : 's are'} more than 15 minutes past their next run. `}
          {queueStuck && 'Ticket events have been waiting for more than 5 minutes. '}
          The background worker may not be running.
        </Alert>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Scheduled jobs" value={formatNumber(data.summary.jobs)} sub={`${formatNumber(data.summary.enabled)} turned on`} icon={<Activity />} />
        <StatCard
          label="Failing"
          value={formatNumber(data.summary.failing)}
          sub="The last run failed"
          icon={<CircleAlert />}
          tone={data.summary.failing ? 'red' : 'green'}
        />
        <StatCard
          label="Overdue"
          value={formatNumber(data.summary.overdue)}
          sub="More than 15 minutes late"
          icon={<Hourglass />}
          tone={data.summary.overdue ? 'red' : 'green'}
        />
        <StatCard
          label="Ticket events waiting"
          value={formatNumber(data.queue.queuedEvents)}
          sub={`${formatNumber(data.queue.waitingRuns)} workflows paused on a wait · ${formatNumber(data.queue.failedEvents)} failed in 30 days`}
          icon={<Inbox />}
          tone={data.queue.failedEvents ? 'amber' : 'violet'}
        />
      </div>

      <Card>
        <CardHeader
          title="The last 30 days"
          description={`One square per day in ${data.timezone}. Point at a square for details.`}
          actions={<ExportLink report="automation" />}
        />
        <div className="border-b border-slate-100 px-5 py-3">
          <HeatmapLegend />
        </div>
        {groups.length === 0 ? (
          <EmptyState
            icon={<Activity />}
            title="No scheduled jobs yet"
            description="Integration syncs, smart rules, scheduled tickets, and time-based workflows show up here."
          />
        ) : (
          groups.map((group) => (
            <section key={group.kind} className="border-b border-slate-100 last:border-0">
              <h3 className="bg-slate-50/80 px-5 py-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">{JOB_KIND_LABEL[group.kind]}</h3>
              <ul className="divide-y divide-slate-100">
                {group.jobs.map((job) => (
                  <li key={`${job.kind}-${job.id}`} className="grid gap-3 px-5 py-3 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] lg:gap-6">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                        <Link to={JOB_LINK[job.kind](job)} className="hover:text-brand-700 hover:underline">
                          {job.name}
                        </Link>
                        {!job.enabled && <Badge>Off</Badge>}
                        {job.overdue && <Badge tone="red">Overdue</Badge>}
                        {job.enabled && job.lastStatus === 'FAILED' && <Badge tone="red">Failing</Badge>}
                      </p>
                      <p className="truncate text-xs text-slate-500" title={job.schedule}>
                        {job.schedule}
                      </p>
                      <p className="text-xs text-slate-500">
                        {job.lastRunAt ? `Last ran ${formatRelative(job.lastRunAt)}` : 'Hasn’t run yet'}
                        {job.nextRunAt && ` · next ${formatRelative(job.nextRunAt)}`}
                      </p>
                      {job.lastStatus === 'FAILED' && job.lastMessage && <p className="mt-0.5 line-clamp-2 text-xs text-red-700">{job.lastMessage}</p>}
                    </div>
                    <div className="self-center">
                      <HeatmapRow cells={job.days} />
                      <div className="mt-1 flex justify-between gap-2 text-[10px] text-slate-400">
                        <span>{formatHeatmapDay(data.days[0])}</span>
                        <span>
                          {formatNumber(job.runs)} run{job.runs === 1 ? '' : 's'}
                          {job.failures > 0 && ` · ${formatNumber(job.failures)} failed`}
                        </span>
                        <span>Today</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'maintenance' | 'costs' | 'replacement' | 'technicians' | 'satisfaction' | 'time' | 'volume' | 'automation';

/** Help desk reports. Under /facilities, the same reports for facilities, plus maintenance, costs, and replacement planning. */
export function ReportsPage() {
  const desk = useDesk();
  const facilities = desk.facilities;
  const [state, setState] = useUrlState({ tab: 'overview', days: '90', department: '' });
  const departments = useDepartments();
  const staff = desk.can('TECHNICIAN');
  const tabs: { value: Tab; label: string }[] = [
    { value: 'overview', label: 'Overview' },
    ...(facilities
      ? [
          { value: 'maintenance' as const, label: 'Maintenance & inspections' },
          { value: 'costs' as const, label: 'Costs' },
          { value: 'replacement' as const, label: 'Replacement forecast' },
        ]
      : []),
    ...(staff ? [{ value: 'technicians' as const, label: 'Technicians' }] : []),
    { value: 'satisfaction', label: 'Satisfaction' },
    ...(staff ? [{ value: 'time' as const, label: 'Time logged' }] : []),
    { value: 'volume', label: facilities ? 'Request volume' : 'Ticket volume' },
    ...(staff && !facilities ? [{ value: 'automation' as const, label: 'Automation status' }] : []),
  ];
  const tab = (tabs.some((t) => t.value === state.tab) ? state.tab : 'overview') as Tab;
  const days = (['30', '90', '365'].includes(state.days) ? Number(state.days) : 90) as ReportDays;
  const departmentId = departments.data?.some((d) => d.id === state.department) ? state.department : undefined;
  const filters: Filters = { days, departmentId };

  return (
    <>
      <PageHeader
        title={facilities ? 'Facilities reports' : 'Reports'}
        description={
          facilities
            ? 'Trends, on-time maintenance, inspection results, and where time, labor, and materials go, for budget planning and replacement decisions.'
            : 'Volume, speed, workload, and satisfaction, so questions about staffing and support get answered with numbers.'
        }
        actions={
          tab !== 'automation' &&
          tab !== 'replacement' && (
            <a href={apiUrl('/reports/export/tickets.csv', { service: desk.service, days, departmentId })}>
              <Button icon={<Download />}>{facilities ? 'Export work orders' : 'Export tickets'}</Button>
            </a>
          )
        }
      />
      <Tabs<Tab> className="mb-4" value={tab} onChange={(value) => setState({ tab: value })} tabs={tabs} />
      {tab !== 'automation' && tab !== 'replacement' && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Segmented<string>
            className="w-full sm:w-80"
            value={String(days)}
            onChange={(value) => setState({ days: value })}
            options={[
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: '365', label: 'Last 365 days' },
            ]}
          />
          <Select className="w-full sm:w-64" value={departmentId ?? ''} onChange={(e) => setState({ department: e.target.value })} aria-label={facilities ? 'Category' : 'Department'}>
            <option value="">{facilities ? 'All categories' : 'All departments'}</option>
            {departments.data?.map((department) => (
              <option key={department.id} value={department.id}>
                {indentedName(department)}
              </option>
            ))}
          </Select>
        </div>
      )}
      {tab === 'overview' && <OverviewTab filters={filters} />}
      {tab === 'maintenance' && <MaintenanceReportTab filters={filters} />}
      {tab === 'costs' && (
        <div className="space-y-6">
          {desk.can('ADMIN') && <HourlyRateCard />}
          <CostsReportTab filters={filters} />
        </div>
      )}
      {tab === 'replacement' && <ReplacementReportTab />}
      {tab === 'technicians' && <TechniciansTab filters={filters} />}
      {tab === 'satisfaction' && <SatisfactionTab filters={filters} />}
      {tab === 'time' && <TimeTab filters={filters} />}
      {tab === 'volume' && <VolumeTab filters={filters} />}
      {tab === 'automation' && <AutomationTab />}
    </>
  );
}
